import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Users, Search, Phone, Download, Filter, Heart, MapPin, Clock,
    CheckCircle, XCircle, DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import CustomerProfileSheet from "@/components/CustomerProfileSheet";

const fmt = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            day: "2-digit", month: "short", year: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
};

const timeAgo = (iso) => {
    if (!iso) return "—";
    try {
        const t = new Date(iso).getTime();
        const diff = Date.now() - t;
        const mins = Math.floor(diff / 60000);
        if (mins < 60) return `${mins}m`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h`;
        const days = Math.floor(hrs / 24);
        if (days < 30) return `${days}d`;
        return `${Math.floor(days / 30)}mo`;
    } catch { return "—"; }
};

const Stat = ({ label, value, sub, tone = "default" }) => {
    const t = tone === "good" ? "text-emerald-300" : tone === "warn" ? "text-yellow-300" : "text-white";
    return (
        <Card className="bg-paper border-border p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-heading">{label}</div>
            <div className={`mt-2 text-3xl font-heading font-black ${t}`}>{value}</div>
            {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
        </Card>
    );
};

const Customers = () => {
    const { token } = useAuth();
    const authH = () => ({ Authorization: `Bearer ${token}` });

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [totals, setTotals] = useState({ all: 0, active: 0, paid: 0, unpaid: 0, opted_out: 0 });
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        status: "all", paid: "all", team: "", region: "",
        joined_within: "", inactive_days: "", search: "",
    });
    const [regions, setRegions] = useState([]);
    const [teams, setTeams] = useState([]);
    const [profilePhone, setProfilePhone] = useState(null);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: "200" });
            ["status", "paid", "team", "region", "search"].forEach((k) => {
                if (filters[k] && filters[k] !== "all") params.set(k, filters[k]);
            });
            if (filters.joined_within) params.set("joined_within", filters.joined_within);
            if (filters.inactive_days) params.set("inactive_days", filters.inactive_days);
            const [list, regs, ts] = await Promise.all([
                axios.get(`${API}/admin/customers?${params.toString()}`, { headers: authH() }),
                axios.get(`${API}/admin/customers/regions`, { headers: authH() }).catch(() => ({ data: [] })),
                axios.get(`${API}/audiences/teams`, { headers: authH() }).catch(() => ({ data: [] })),
            ]);
            setItems(list.data.items || []);
            setTotal(list.data.total || 0);
            setTotals(list.data.totals || {});
            setRegions(regs.data || []);
            setTeams(ts.data || []);
        } catch (e) {
            toast.error("Failed to load customers");
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [filters.status, filters.paid, filters.team, filters.region, filters.joined_within, filters.inactive_days]);

    const downloadCsv = async () => {
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => {
                if (v && v !== "all") params.set(k, v);
            });
            const res = await axios.get(`${API}/admin/customers/export.csv?${params.toString()}`, {
                headers: authH(), responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `dimbani-customers-${Date.now()}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch { toast.error("Download failed"); }
    };

    const setF = (patch) => setFilters((p) => ({ ...p, ...patch }));

    return (
        <AdminLayout title="Customers">
            <div className="space-y-6" data-testid="customers-page">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="font-heading text-2xl font-black flex items-center gap-2">
                            <Users className="h-6 w-6 text-electric" />
                            Customers directory
                        </h2>
                        <p className="text-sm text-white/60 mt-1">
                            Every phone we know about, merged across subscriptions, SMS chats, customer bases, and transactions.
                        </p>
                    </div>
                    <Button onClick={downloadCsv} variant="outline" className="border-border" data-testid="download-csv-btn">
                        <Download className="h-4 w-4 mr-2" /> Download CSV
                    </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Stat label="Customers" value={totals.all} sub={`${total} match filters`} />
                    <Stat label="Active subs" value={totals.active} tone="good" />
                    <Stat label="Paid" value={totals.paid} tone="good" />
                    <Stat label="Unpaid" value={totals.unpaid} />
                    <Stat label="Opted-out" value={totals.opted_out} tone="warn" />
                </div>

                <Card className="bg-paper border-border p-4" data-testid="filters-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Filter className="h-4 w-4 text-electric" />
                        <span className="font-heading font-bold text-sm uppercase tracking-wider">Filters</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                        <Select value={filters.status} onValueChange={(v) => setF({ status: v })}>
                            <SelectTrigger className="bg-obsidian border-border text-white text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All statuses</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="opted_out">Opted-out</SelectItem>
                                <SelectItem value="none">No subscription</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filters.paid} onValueChange={(v) => setF({ paid: v })}>
                            <SelectTrigger className="bg-obsidian border-border text-white text-xs" data-testid="filter-paid"><SelectValue placeholder="Paid" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="paid">Paid</SelectItem>
                                <SelectItem value="unpaid">Unpaid</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filters.team || "all-teams"} onValueChange={(v) => setF({ team: v === "all-teams" ? "" : v })}>
                            <SelectTrigger className="bg-obsidian border-border text-white text-xs" data-testid="filter-team"><SelectValue placeholder="Team" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all-teams">All teams</SelectItem>
                                {teams.map((t) => <SelectItem key={t.team} value={t.team}>{t.team}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={filters.region || "all-regions"} onValueChange={(v) => setF({ region: v === "all-regions" ? "" : v })}>
                            <SelectTrigger className="bg-obsidian border-border text-white text-xs" data-testid="filter-region"><SelectValue placeholder="Region" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all-regions">All regions</SelectItem>
                                {regions.map((r) => <SelectItem key={r.region} value={r.region}>{r.region} ({r.count})</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Input placeholder="Joined in last N days"
                               type="number" value={filters.joined_within} onChange={(e) => setF({ joined_within: e.target.value })}
                               className="bg-obsidian border-border text-white text-xs" data-testid="filter-joined" />
                        <Input placeholder="Inactive ≥ N days"
                               type="number" value={filters.inactive_days} onChange={(e) => setF({ inactive_days: e.target.value })}
                               className="bg-obsidian border-border text-white text-xs" data-testid="filter-inactive" />
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
                            <Input placeholder="Search phone / team / region"
                                   value={filters.search} onChange={(e) => setF({ search: e.target.value })}
                                   onKeyDown={(e) => e.key === "Enter" && fetchAll()}
                                   className="pl-7 bg-obsidian border-border text-white text-xs" data-testid="filter-search" />
                        </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                        <Button size="sm" onClick={fetchAll} className="bg-electric hover:bg-electric/90" data-testid="apply-btn">Apply</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setFilters({ status: "all", paid: "all", team: "", region: "", joined_within: "", inactive_days: "", search: "" }); }}>Reset</Button>
                    </div>
                </Card>

                <Card className="bg-paper border-border p-0 overflow-hidden">
                    <div className="overflow-x-auto" data-testid="customers-table">
                        <table className="w-full text-sm">
                            <thead className="bg-obsidian text-white/60 text-[10px] uppercase tracking-wider">
                                <tr>
                                    <th className="px-3 py-2 text-left">Phone</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                    <th className="px-3 py-2 text-left">Paid</th>
                                    <th className="px-3 py-2 text-left">Teams</th>
                                    <th className="px-3 py-2 text-left">Region</th>
                                    <th className="px-3 py-2 text-left">Bases</th>
                                    <th className="px-3 py-2 text-right">Interactions</th>
                                    <th className="px-3 py-2 text-right">Last active</th>
                                    <th className="px-3 py-2 text-right">Joined</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {loading && <tr><td colSpan={9} className="p-10 text-center text-white/50">Loading…</td></tr>}
                                {!loading && items.length === 0 && (
                                    <tr><td colSpan={9} className="p-10 text-center text-white/50">No customers match these filters.</td></tr>
                                )}
                                {!loading && items.map((c) => (
                                    <tr key={c.phone_number} className="hover:bg-subtle/40" data-testid={`row-${c.phone_number}`}>
                                        <td className="px-3 py-2">
                                            <button
                                                onClick={() => setProfilePhone(c.phone_number)}
                                                className="font-mono text-xs text-white hover:text-electric underline-offset-2 hover:underline"
                                                data-testid={`open-profile-${c.phone_number}`}
                                            >
                                                {c.phone_number}
                                            </button>
                                        </td>
                                        <td className="px-3 py-2">
                                            <Badge className={
                                                c.sub_status === "active" ? "bg-emerald-500/15 text-emerald-300 border-0" :
                                                c.sub_status === "opted_out" ? "bg-red-500/15 text-red-300 border-0" :
                                                c.sub_status === "pending" ? "bg-yellow-500/15 text-yellow-300 border-0" :
                                                "bg-white/10 text-white/60 border-0"
                                            }>{c.sub_status}</Badge>
                                        </td>
                                        <td className="px-3 py-2">
                                            {c.paid ? (
                                                <span className="inline-flex items-center gap-1 text-emerald-300 text-xs"><CheckCircle className="h-3 w-3" /> {c.total_paid} TZS</span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-white/40 text-xs"><XCircle className="h-3 w-3" /> No</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-white/80">
                                            {c.teams.slice(0, 2).map((t) => <Badge key={t} variant="outline" className="border-border mr-1 text-[10px]"><Heart className="h-2.5 w-2.5 mr-0.5" />{t}</Badge>)}
                                            {c.teams.length > 2 && <span className="text-white/40 text-[10px]">+{c.teams.length - 2}</span>}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-white/70">
                                            {c.regions.slice(0, 2).map((r) => <span key={r} className="inline-flex items-center gap-0.5 mr-1"><MapPin className="h-2.5 w-2.5 text-electric" />{r}</span>)}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-white/60">{c.bases.slice(0, 1).join(", ")}{c.bases.length > 1 && <span className="text-white/40"> +{c.bases.length - 1}</span>}</td>
                                        <td className="px-3 py-2 text-right text-xs text-white/80">{c.interactions}</td>
                                        <td className="px-3 py-2 text-right text-xs text-white/60" title={fmt(c.last_active)}>{timeAgo(c.last_active)}</td>
                                        <td className="px-3 py-2 text-right text-xs text-white/60" title={fmt(c.first_joined)}>{timeAgo(c.first_joined)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>

            <CustomerProfileSheet
                phone={profilePhone}
                open={!!profilePhone}
                onOpenChange={(o) => { if (!o) setProfilePhone(null); }}
            />
        </AdminLayout>
    );
};

export default Customers;
